
import * as React from "react"
import { cn } from "@/lib/utils"
import { useSidebar } from "./sidebar-provider"

const Sidebar = ({ className, children, ...props }: React.HTMLAttributes<HTMLElement>) => {
  const { isCollapsed, isMobile, setCollapsed } = useSidebar()
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const startY = React.useRef(0)

  // Swipe-down to close
  const handleTouchStart = React.useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = React.useCallback((e: React.TouchEvent) => {
    const deltaY = e.changedTouches[0].clientY - startY.current
    if (deltaY > 80) {
      setCollapsed(true)
    }
  }, [setCollapsed])

  if (isMobile) {
    return (
      <>
        {/* Backdrop overlay */}
        {!isCollapsed && (
          <div
            className="fixed inset-0 z-40 bg-black/40"
            onClick={() => setCollapsed(true)}
          />
        )}
        <div
          ref={sheetRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className={cn(
            "fixed bottom-0 left-0 right-0 z-50 flex flex-col bg-background border-t rounded-t-2xl shadow-2xl transition-transform duration-300 max-h-[60vh] overflow-hidden",
            isCollapsed ? "translate-y-full" : "translate-y-0",
            className
          )}
          {...props}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-2 pb-1 cursor-grab">
            <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
          </div>
          <div className="flex-1 overflow-y-auto overscroll-contain pb-2">
            {children}
          </div>
        </div>
      </>
    )
  }

  return (
    <aside 
      className={cn(
        "flex flex-col h-screen bg-secondary border-r transition-all duration-300", 
        isCollapsed ? "w-0 overflow-hidden opacity-0" : "w-[250px]",
        className
      )} 
      {...props}
    >
      {children}
    </aside>
  )
}
Sidebar.displayName = "Sidebar"

export { Sidebar }
